import { CommonModule } from '@angular/common';
import { Component, Inject, inject, OnInit, DOCUMENT } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { DomUtils } from '@igo2/utils';
import { BehaviorSubject, delay, filter, first, map, Subject, take, takeUntil } from 'rxjs';
import { HeaderComponent } from './pages/header/header.component';
import { ZzzConfigService } from './zzz/core/config/config.service';
import { Media, MediaService } from '@igo2/core/media';
import { SigtMenusComponent } from './integration/sigt-menus/sigt-menus.component';
import { SigtChargementEnCoursComponent } from './integration/sigt-chargement-en-cours/sigt-chargement-en-cours.component';
import { MessageService } from '@igo2/core/message';
import { provideNativeDateAdapter } from '@angular/material/core';
import { ToolState } from '@igo2/integration';
import { ToastLayoutService } from './shared/services/custom-toast-layout.service';
import { ToastrService } from 'ngx-toastr';
import { ConsultationsPubliqueService } from './shared/services/pilotage-consultation-publique.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        CommonModule,
        RouterModule,
        HeaderComponent,
        SigtMenusComponent,
        SigtChargementEnCoursComponent
    ],
    providers: [
        provideNativeDateAdapter()
    ]
})
export class AppComponent implements OnInit {

  #router = inject(Router);
  #activatedRoute = inject(ActivatedRoute);
  #zzzConfigService = inject(ZzzConfigService);
  #mediaService = inject(MediaService);
  #document = inject(DOCUMENT);
  #messageService = inject(MessageService);
  isCarteLegere$ = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();


  cacherEntete$ = this.#router.events
  .pipe(
    filter((event) => event instanceof NavigationEnd),
    map(() => this.#activatedRoute),
    map((route) => {
      while (route.firstChild) {
        route = route.firstChild;
      }

      return route;
    }),
    filter((route) => route.outlet === 'primary'),
    map((route) => route.snapshot.data['cacherEntete'])
  );

  constructor(
    private toastLayoutService: ToastLayoutService,
    private toastr: ToastrService,
    private toolState: ToolState,
    private consultationService: ConsultationsPubliqueService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.#handleSplashScreen();
    this.isCarteLegere$.next(
      !this.#zzzConfigService.get().estIntranet
      && !this.#zzzConfigService.get().estPartenaire
    );
    //Remove the meta noindex if it is not legere
    this.isCarteLegere$.subscribe(isCarteLegere => {
      if (!isCarteLegere) {
        const meta = DomUtils.create(this.#document, 'meta', { name: "robots", content: "noindex" });
        this.#document.head.appendChild(meta);
      }
    });
  }

  ngAfterViewInit(): void {
    var erreur = localStorage.getItem('SIGT-Error');
    if (erreur) {
      localStorage.removeItem('SIGT-Error');
      setTimeout(() => this.#messageService.error(erreur), 500);
    }

     if (this.isCarteLegere$.value) {
      this.toastLayoutService.initializeToastLayout().pipe(take(1)).subscribe(() => {
        this.AfficherMessageConsultationsPubliques();
      });
     }
  }

  private AfficherMessageConsultationsPubliques() {
    this.consultationService.getMessageBaniere()
      .pipe(takeUntil(this.destroy$))
      .subscribe(baniere => {
        if (baniere) {
          var options = {
            tapToDismiss: false,
            timeOut: 0,
            extendedTimeOut: 0,
            closeButton: true,
            progressBar: true,
            toastClass: 'ngx-toastr map-banner-toast',
            positionClass: 'map-banner-toast-position',
            enableHtml: true
          };
          this.toastr.info(baniere.message, baniere.titre, options);

          //Ajouter hyperlien dans la banière pour ouvrir l'outil d'inscription de commentaire
          const divToastContainer =  this.document.getElementById('banner-toast-container');
          divToastContainer.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            if (target?.classList.contains('open-comment-tool')) {
              event.preventDefault();
              event.stopPropagation();
              this.toolState.toolToActivateFromOptions({
                tool: 'sigt-inscrire-commentaire',
                options: null
              });
            }
          });
        }
      });
  }

  #handleSplashScreen(): void {
    this.#router.events
      .pipe(
        first((events) => events instanceof NavigationEnd),
        delay(500)
      )
      .subscribe(() => {
        this.#removeSplashScreen();
      });
  }

  #removeSplashScreen(): void {
    const intro = this.#document.getElementById('splash-screen');

    if (!intro) {
      return;
    }

    intro.classList.add('is-destroying');

    const destroyingAnimationTime = 300;
    const stylesheet = this.#document.getElementById('splash-screen-stylesheet');

    setTimeout(() => {
      DomUtils.remove(intro);
      DomUtils.remove(stylesheet);
    }, destroyingAnimationTime);
  }

  isMobile(): boolean {
    return this.#mediaService.getMedia() === Media.Mobile;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
